import { FormikProps } from 'formik'
import { getFilterLocations } from './utils'
import { startSxpProxy } from '../../utils/api'
import { useEffect, useCallback, useState } from 'react'
import { searchPatient } from '../../services/ProrithmServices'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectPatientInfo, setPatientInfo } from './ProrithmSlice'
import { LocationData, selectLocations } from '../location/locationSlice'
import { Box, MenuItem, Typography, SelectChangeEvent } from '@mui/material'
import {
  Beds,
  Rooms,
  Wards,
  messages,
  constants,
  MapToIpdValues,
  INTENT_GET_WARD_ROOM_BED_DETAILS,
} from './Constants'
import {
  IPD_PROJECT_ID,
  CRITICAL_BED_ID,
  NON_CRITICAL_BED_ID,
} from '../../utils/constants'
import SelectedPatient from './SelectedPatient'
import SelectField from './MappingForms/SelectField'

interface SearchByBedProps {
  onBedSelection?: (bedData: {
    location: string
    bedType: string
    ward: string
    room: string
    bed: string
    wardName: string
    roomName: string
    bedNumber: string
  }) => void
  formik?: FormikProps<MapToIpdValues>
}

const SearchByBed: React.FC<SearchByBedProps> = ({
  onBedSelection,
  formik,
}) => {
  const patientInfo = useAppSelector(selectPatientInfo)
  const dispatch = useAppDispatch()
  const locations = useAppSelector(selectLocations)
  const activeLocations = locations
    ?.filter(
      (l: LocationData) => l?.resource?.status === constants?.activeStatus
    )
    ?.filter(
      (item) => !getFilterLocations()?.includes(item?.resource?.name ?? '')
    )

  const [wards, setWards] = useState<Wards[]>([])
  const [rooms, setRooms] = useState<Rooms[]>([])
  const [beds, setBeds] = useState<Beds[]>([])

  const handleWardChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      formik?.handleChange(event)
      formik?.setFieldValue('room', '')
      formik?.setFieldValue('bed', '')
    },
    [formik]
  )

  const resetBedFields = () => {
    formik?.setFieldValue('ward', '')
    formik?.setFieldValue('room', '')
    formik?.setFieldValue('bed', '')
  }

  const saveFormDataOnBedSelection = () => {
    if (
      onBedSelection &&
      formik?.values?.bed &&
      wards?.length &&
      rooms?.length &&
      beds?.length
    ) {
      // set form data for bed selection
      const wardName =
        wards?.find((w) => w?.id === Number(formik?.values?.ward))?.ward_name ??
        ''
      const roomName =
        rooms?.find((r) => r?.id === Number(formik?.values?.room))?.room_name ??
        ''
      const bedNumber =
        beds?.find((b) => b?.id === Number(formik?.values?.bed))?.bed_number ??
        ''

      onBedSelection({
        location: formik.values.location,
        bedType: formik.values.bedType,
        ward: formik.values.ward,
        room: formik.values.room,
        bed: formik.values.bed,
        wardName,
        roomName,
        bedNumber,
      })
    }
  }

  const handleBedChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      formik?.handleChange(event)
    },
    [formik]
  )

  const getBedDetails = useCallback(() => {
    if (!formik?.values?.location || !formik?.values?.bedType) return

    const intent = INTENT_GET_WARD_ROOM_BED_DETAILS
    const state = {
      status: constants?.all,
      locationName: formik?.values?.location,
      bedTypeId: formik?.values?.bedType,
    }
    startSxpProxy(IPD_PROJECT_ID, intent, state)
      .then((data) => {
        setWards(data?.data?.data?.wards ?? [])
        setRooms(data?.data?.data?.rooms ?? [])
        setBeds(data?.data?.data?.beds ?? [])
      })
      .catch((err) => {
        console.error(err)
      })
  }, [formik?.values?.location, formik?.values?.bedType])

  const fetchPatientDetails = useCallback(async () => {
    if (formik?.values?.bed) {
      const data = await searchPatient('bedId', formik?.values?.bed, true)
      if (data?.success && data?.data?.length > 0) {
        dispatch(setPatientInfo(data?.data?.[0]))
      } else {
        console.warn(messages?.patientNotFound)
        dispatch(setPatientInfo(null))
      }
    }
  }, [dispatch, formik?.values?.bed])

  useEffect(() => {
    if (formik?.values?.bed) {
      // Get patient details when bed is selected
      fetchPatientDetails()
    }
  }, [fetchPatientDetails, formik?.values?.bed])

  useEffect(() => {
    if (formik?.values?.location && formik?.values?.bedType) {
      // Fetch bed details when location or bed type changes
      getBedDetails()
      resetBedFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.location, formik?.values?.bedType])

  useEffect(() => {
    saveFormDataOnBedSelection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.bed])

  if (!formik) {
    return <div>{messages?.formRenderFailed}</div>
  }

  const fieldConfig = [
    {
      name: 'location',
      id: 'location-select',
      onBlur: formik?.handleBlur,
      onChange: formik?.handleChange,
      value: formik?.values?.location || '',
      labelId: 'location-select-label',
      label: constants?.selectLocation,
      error: formik?.touched?.location && formik?.errors?.location,
      disabled: false,
      inputProps: { 'aria-label': constants?.selectLocation },
      children: activeLocations?.map((d: LocationData) => (
        <MenuItem key={d?.resource?.id} value={d?.resource?.name}>
          {d?.resource?.name}
        </MenuItem>
      )),
    },
    {
      name: 'bedType',
      id: 'bedtype-select',
      labelId: 'bedtype-label',
      label: constants?.bedType,
      value: formik?.values?.bedType || '',
      onChange: formik?.handleChange,
      onBlur: formik?.handleBlur,
      inputProps: { 'aria-label': constants?.bedType },
      error: formik?.touched?.bedType && formik?.errors?.bedType,
      disabled: !formik?.values?.location,
      children: [
        <MenuItem key='critical' value={CRITICAL_BED_ID}>
          {constants?.critical}
        </MenuItem>,
        <MenuItem key='nonCritical' value={NON_CRITICAL_BED_ID}>
          {constants?.nonCritical}
        </MenuItem>,
      ],
    },
    {
      name: 'ward',
      id: 'ward-select',
      labelId: 'ward-label',
      onChange: handleWardChange,
      onBlur: formik?.handleBlur,
      value: formik?.values?.ward || '',
      label: constants?.selectWard,
      inputProps: { 'aria-label': constants?.selectWard },
      error: formik?.touched?.ward && formik?.errors?.ward,
      disabled: !formik?.values?.bedType,
      children: wards?.map((item) => (
        <MenuItem key={item?.id} value={item?.id?.toString()}>
          {item?.ward_name}
        </MenuItem>
      )),
    },
    {
      name: 'room',
      id: 'room-select',
      labelId: 'room-label',
      onBlur: formik?.handleBlur,
      value: formik?.values?.room || '',
      label: constants?.selectRoom,
      onChange: formik?.handleChange,
      error: formik?.touched?.room && formik?.errors?.room,
      disabled: !formik?.values?.ward,
      inputProps: { 'aria-label': constants?.selectRoom },
      children: rooms
        ?.filter(
          (item) =>
            formik?.values?.ward?.toString() === item?.ward_id?.toString()
        )
        ?.map((item) => (
          <MenuItem key={item?.id} value={item?.id?.toString()}>
            {item?.room_name}
          </MenuItem>
        )),
    },
    {
      name: 'bed',
      id: 'bed-select',
      labelId: 'bed-label',
      label: constants?.selectBed,
      value: formik?.values?.bed || '',
      onBlur: formik?.handleBlur,
      onChange: handleBedChange,
      error: formik?.touched?.bed && formik?.errors?.bed,
      inputProps: { 'aria-label': constants?.selectBed },
      disabled: !formik?.values?.room,
      children: beds
        ?.filter(
          (item) =>
            formik?.values?.room?.toString() === item?.room_id?.toString()
        )
        ?.map((item) => (
          <MenuItem key={item?.id} value={item?.id?.toString()}>
            {item?.bed_number}
          </MenuItem>
        )),
    },
  ]

  return (
    <>
      <Box display='flex' flexDirection='row' flexWrap='wrap' marginY={2}>
        {fieldConfig?.map((field) => (
          <SelectField key={field?.name} {...field} />
        ))}
      </Box>
      {formik?.values?.bed &&
        (patientInfo ? (
          <SelectedPatient mapTo={constants?.ipd} />
        ) : (
          <Typography variant='body2' marginTop={2} id='no-patient-found-msg'>
            {messages?.noPatient}
          </Typography>
        ))}
    </>
  )
}

export default SearchByBed
